/**
 * Every module registry the web reads, DERIVED from a module tree.
 *
 * Before the catalog (2026-10-15) these were kept by hand in eight places
 * (brief §2.1) that all had to agree. Each is now one function of a
 * `ModuleTree`, and the constants the app imports (`MODULE_NAV`,
 * `MODULE_LABEL`, `moduleAccent`, `TILE_VISIBILITY_RULES`, …, still exported
 * from their old homes) are those functions applied to the default tree.
 * When the tenant module config arrives (step 3), a tree resolved with it goes
 * through the same functions; nothing here changes.
 *
 * This module must stay free of React and of the files that re-export it
 * (tokens.ts, shell/*, auth/visibility.ts, shared/icons), so any of them can
 * import it without a cycle.
 */
import type { ModuleSlug, TileSlug } from './ids';
import type { ModuleTree, TreeModule } from './catalog';

// ── Shapes (re-exported from their historical homes) ────────────────────

export interface NavItem {
  /** Full route path. Always absolute, always starts with `/`. */
  to: string;
  /** Human label, mirrors the PHP `label` field where one exists. */
  label: string;
  /** Optional caption rendered beneath the label. */
  subtitle?: string;
  /**
   * Only render this sub-item when the user can see the given tile (HR ▸
   * Cash claims: the Appraisals audience must not see links that 403).
   */
  requiresTile?: string;
  /** Only render when the user has HR ▸ Appraisals access. */
  requiresAppraisals?: boolean;
}

export interface NavGroup {
  /** Group heading shown above its items. Omit for ungrouped lists. */
  heading?: string;
  /**
   * The group's ink: a `--c-*` token NAME (never a hex) that the rail binds
   * to `--accent` on the group wrapper. Omit to inherit the module accent.
   */
  accent?: string;
  items: NavItem[];
}

/** One entry of the sidebar's module switcher. */
export interface ModuleDef {
  slug: ModuleSlug;
  to: string;
  label: string;
  /** One-line description shown under the module in the switcher. */
  blurb: string;
  tile: string;
  /**
   * HR is admin-only as a tile, but its Appraisals page reaches a broad
   * audience. When set, the entry is visible if `canSeeTile(tile)` OR the
   * user has appraisal access, and non-admins are pointed at `nonAdminTo`.
   */
  appraisalsAware?: boolean;
  nonAdminTo?: string;
}

export interface TileVisibilityRule {
  allowedRoles: string[];
  ownerOnly?: boolean;
  alwaysVisible?: boolean;
  /**
   * Evaluate purely by `allowedRoles`, ignoring `allowed_tools` membership
   * (Employee Hub). Unlike `alwaysVisible`, it still respects the role list.
   */
  roleOnly?: boolean;
}

/** One Hub tile (the home page's grid). */
export interface HubTile {
  /** The tile slug, printed on the tile and checked by `canSeeTile`. */
  slug: string;
  to: string;
  label: string;
  hint: string;
  /** `var(--ink)`. */
  accent: string;
  /** Set for shell modules (the tile draws its `ModuleIcon`). */
  mod?: ModuleSlug;
  /** The `MODULE_ICONS` key, for a surface outside the shell. */
  icon: string;
  surface: TreeModule['surface'];
  appraisalsAware?: boolean;
  nonAdminTo?: string;
}

// ── Helpers ─────────────────────────────────────────────────────────────

function shell(tree: ModuleTree): TreeModule[] {
  return tree.modules.filter((m) => m.surface === 'shell');
}

function byShellId<T>(tree: ModuleTree, f: (m: TreeModule) => T): Record<ModuleSlug, T> {
  return Object.fromEntries(shell(tree).map((m) => [m.id, f(m)])) as Record<ModuleSlug, T>;
}

function inSearchOrder(tree: ModuleTree): TreeModule[] {
  const byId = new Map(tree.modules.map((m) => [m.id, m]));
  return tree.searchOrder.map((id) => byId.get(id)).filter((m): m is TreeModule => !!m);
}

// ── The derivations ─────────────────────────────────────────────────────

/** Module slug → `var(--ink)` (`moduleAccent`). */
export function deriveModuleAccent(tree: ModuleTree): Record<ModuleSlug, string> {
  return byShellId(tree, (m) => `var(${m.ink})`);
}

/** Module slug → `var(--ink-rgb)` (`moduleAccentRgb`). */
export function deriveModuleAccentRgb(tree: ModuleTree): Record<ModuleSlug, string> {
  return byShellId(tree, (m) => `var(${m.ink}-rgb)`);
}

/** Module slug → its sidebar / breadcrumb label (`MODULE_LABEL`). */
export function deriveModuleLabels(tree: ModuleTree): Record<ModuleSlug, string> {
  return byShellId(tree, (m) => m.navLabel);
}

/** Module slug → tile slug (`MODULE_TILE`). */
export function deriveModuleTiles(tree: ModuleTree): Record<ModuleSlug, string> {
  return byShellId(tree, (m) => m.tile);
}

/** Module slug → `MODULE_ICONS` key (`ICON_FOR_MODULE`). */
export function deriveModuleIcons(tree: ModuleTree): Record<ModuleSlug, string> {
  return byShellId(tree, (m) => m.icon);
}

/** The sidebar's module switcher (`MODULES`), switched-on modules only. */
export function deriveSwitcher(tree: ModuleTree): ModuleDef[] {
  return shell(tree)
    .filter((m) => m.enabled && m.switcher && !m.hostedIn)
    .map((m) => {
      const d: ModuleDef = {
        slug: m.id as ModuleSlug,
        to: m.route,
        label: m.navLabel,
        blurb: m.blurb ?? '',
        tile: m.tile,
      };
      if (m.appraisalsAware) d.appraisalsAware = true;
      if (m.nonAdminRoute) d.nonAdminTo = m.nonAdminRoute;
      return d;
    });
}

/**
 * Each shell module's rail (`MODULE_NAV`): its sections in order, each with
 * the switched-on pages that are rail rows; a section left with no row is
 * not drawn. Keyed in `searchOrder`.
 */
export function deriveModuleNav(tree: ModuleTree): Record<ModuleSlug, NavGroup[]> {
  const out = {} as Record<ModuleSlug, NavGroup[]>;
  for (const m of inSearchOrder(tree)) {
    if (m.surface !== 'shell') continue;
    const groups: NavGroup[] = [];
    for (const g of m.groups) {
      const items: NavItem[] = [];
      for (const p of g.pages) {
        if (p.navHidden || !p.enabled) continue;
        const item: NavItem = { to: p.to, label: p.label };
        if (p.subtitle) item.subtitle = p.subtitle;
        if (p.requires?.tile) item.requiresTile = p.requires.tile;
        if (p.requires?.appraisals) item.requiresAppraisals = true;
        items.push(item);
      }
      if (items.length === 0) continue;
      const group = {} as NavGroup;
      if (g.heading) group.heading = g.heading;
      if (g.ink) group.accent = g.ink;
      group.items = items;
      groups.push(group);
    }
    out[m.id as ModuleSlug] = groups;
  }
  return out;
}

/**
 * Route → breadcrumb label for the real, named routes that are not rail rows
 * (`OFF_RAIL_PAGE_LABELS`): the region logs views, the PHP nav-hidden pages.
 */
export function deriveOffRailLabels(tree: ModuleTree): Record<string, string> {
  const out: Record<string, string> = {};
  for (const m of tree.modules) {
    for (const g of m.groups) {
      for (const p of g.pages) {
        if (p.offRail && p.enabled) out[p.to] = p.crumb ?? p.label;
      }
    }
  }
  return out;
}

/** Modules that own a `<slug>/hot-plate` team board, in `searchOrder`. */
export function deriveHotPlateModules(tree: ModuleTree): ModuleSlug[] {
  return inSearchOrder(tree)
    .filter((m) => m.surface === 'shell' && m.hotPlate)
    .map((m) => m.id as ModuleSlug);
}

/** Tile slug → its visibility rule (`TILE_VISIBILITY_RULES`). */
export function deriveTileRules(tree: ModuleTree): Record<TileSlug, TileVisibilityRule> {
  const out = {} as Record<TileSlug, TileVisibilityRule>;
  for (const m of tree.modules) {
    const rule: TileVisibilityRule = { allowedRoles: [...m.roles] };
    if (m.access === 'owner-only') rule.ownerOnly = true;
    if (m.access === 'always') rule.alwaysVisible = true;
    if (m.access === 'role-only') rule.roleOnly = true;
    out[m.tile as TileSlug] = rule;
  }
  return out;
}

/** The Hub home's tiles, switched-on modules only, in Hub order. */
export function deriveHubTiles(tree: ModuleTree): HubTile[] {
  return tree.modules
    .filter((m) => m.enabled && !m.hostedIn && (m.surface === 'shell' || m.surface === 'standalone'))
    .map((m) => {
      const t: HubTile = {
        slug: m.tile,
        to: m.route,
        label: m.label,
        hint: m.hint,
        accent: `var(${m.ink})`,
        icon: m.icon,
        surface: m.surface,
      };
      if (m.surface === 'shell') t.mod = m.id as ModuleSlug;
      if (m.appraisalsAware) t.appraisalsAware = true;
      if (m.nonAdminRoute) t.nonAdminTo = m.nonAdminRoute;
      return t;
    });
}

/**
 * Route → the module whose rail shows it, for the rows a module BORROWS from
 * another (`CatalogPage.borrows`, switched on). On such a route the shell
 * opens the borrowing module (its rail, its ink, its crumb), not the module
 * the URL belongs to: Guest Portal ▸ Content's rows live under /coportal.
 * Empty while no borrowing module is on (Asian Trails).
 */
export function deriveBorrowedRoutes(tree: ModuleTree): Record<string, ModuleSlug> {
  const out: Record<string, ModuleSlug> = {};
  for (const m of tree.modules) {
    if (!m.enabled || m.surface !== 'shell') continue;
    for (const g of m.groups) for (const p of g.pages) if (p.borrows && p.enabled) out[p.to] = m.id as ModuleSlug;
  }
  return out;
}
